<template>
    <form @submit.prevent="saveShipping()" id="shipping_form" name="shipping_form">
        <!-- DROPSHIP -->
        <div class="notification-bar-wrapper">
            <div class="drop-ship-banner">
                <span class="drop-ship-leftside">
                    <p class="title-s banner-title">
                        Drop Ship This Order?
                    </p>
                    <span class="cta-wrapper">
                        <button id="dropship_no" type="button" :class="getToggleButtonClasses({active: !isDropShip})" 
                            @click="setDropShip(0)" aria-label="Decline drop shipping option">
                            No
                        </button>
                        <button id="dropship_yes" type="button" :class="getToggleButtonClasses({active: isDropShip})" 
                            @click="setDropShip(1)" aria-label="Accept drop shipping option">
                            Yes
                        </button>
                    </span>
                    <p class="paragraph-default">We’ll send items directly to your customer from our warehouse for a small additional fee per order</p>
                </span>
            </div>  
        </div>

        <!-- ADDRESS -->
        <div class="b2b-shipping-summary">
            <div class="b2b-card">
                <div class="header">
                    <h2 class="title-l">Shipping Address</h2>
                </div>
                <div class="content">
                    <div class="row">
                        <div class="input-row select-wrapper advertising">
                            <custom-select 
                                name="ship_to_address" 
                                id="ship_to_address" 
                                aria-label="Select a shipping address" 
                                label="Ship To Address"
                                :options="formattedShipTos" 
                                v-model="shipTo"
                            />
                        </div>
                        <div class="input-row dropship-spacing">
                            <div v-if="isDropShip" class="dropship-wrapper">
                                <Checkbox
                                    v-if="!form.shipping_customer_code"
                                    v-model="form.shipping_save_dropship"
                                    label="Save This Address for Future Use"
                                    labelClasses="title-s text-rothcoCharcoal"
                                />
                                <div v-else>
                                    <a @click="setAddress({country: 'USA'})">
                                        Add New Drop-Shipping Address
                                    </a>
                                </div>
                            </div>
                            <div v-else class="dropship-wrapper">
                                <div class="dropship-link">
                                    <a @click="requestNewShippingAddress" aria-label="Request new shipping address">Request New Shipping Address</a>
                                </div>
                                <RedQuestion alt="Question mark icon" />
                            </div>
                        </div>
                    </div>
                    <hr />

                    <AddressInput
                        id="address_input"
                        class="col-span-2"
                        v-model:name="form.shipping_name"
                        v-model:inCareOf="form.shipping_in_care_of"
                        v-model:street1="form.shipping_address"
                        v-model:street2="form.shipping_address_2"
                        v-model:city="form.shipping_city"
                        v-model:state="form.shipping_state"
                        v-model:zip="form.shipping_zip"
                        v-model:country="form.shipping_country"
                        v-model:addressValid="dropshipAddressIsValid"
                        :regions="props.regions"
                        :countries="form.drop_ship == 1 ? [{ alpha3code: 'USA', alpha2code: 'US', name: 'United States' }] : props.countries"
                        :can-edit="form.drop_ship == 1"
                        :errors="addressErrors"
                        :enableValidation="isDropShip"
                    />

                    <div class="input-row">
                        <div class="selector-sendTo-wrapper">
                            <div class="selectore-state select-wrapper country">
                                <custom-select label="Final Destination *" :options="uniqueFinalDestinationOptions" v-model="finalDestinationAddress" :readonly="isDropShip" :error="form.errors.shipping_final_destination_country != null"/>
                                <div class="field-state input-folder"><p class="input-support-text invalid-state">{{ form.errors.shipping_final_destination_country }}</p></div>
                            </div>
                        </div>
                        <div class="selector-sendTo-wrapper">
                            <div class="selectore-state select-wrapper state" style="width: 100%">
                                <custom-select
                                    v-show="form.shipping_final_destination_country == 'USA'"
                                    :readonly="isDropShip"
                                    label="Final Destination State *" :options="formattedFinalDestinationStates" v-model="finalDestinationState"
                                    :error="form.errors.shipping_final_destination_state != null"
                                />
                                <div v-show="form.shipping_final_destination_country == 'USA'" 
                                    class="field-state input-folder">
                                    <p class="input-support-text invalid-state">{{ form.errors.shipping_final_destination_state }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="certify-card flex-column" v-if="form.shipping_final_destination_country && !isExporter">
                        <div class="flex-row">
                            <Checkbox
                                :label="null" class="certify-checkbox"  aria-label="Certify domestic sales" id="final_destination_certify" v-model="options.final_destination_certify"
                            />
                            <div class="certify-text" v-if="form.shipping_final_destination_country == 'USA'">
                                <p class="paragraph-details">I certify that all goods on this order are for domestic sales in the United States and I will
                                    not forward any goods to a freight forwarder or otherwise export these goods outside of the
                                    United States. I understand that diversion contrary to U.S. law is prohibited.</p>
                                <p class="paragraph-details"><b>IMPORTANT:</b> If you intend, or have knowledge of the intent to export this merchandise,
                                    please contact ROTHCO immediately for compliance information.</p>
                            </div>
                            <div class="certify-text" v-else>
                                <p class="paragraph-details">I certify that all goods on this order are for export sales in {{ form.shipping_final_destination_country }} and I will
                                    not forward any goods to a freight forwarder or otherwise export these goods anywhere except for {{ form.shipping_final_destination_country }}. 
                                    I understand that diversion contrary to U.S. law is prohibited.</p>
                                <p class="paragraph-details"><b>IMPORTANT:</b> If you intend, or have knowledge of the intent to export this merchandise anywhere that is not {{ form.shipping_final_destination_country }},
                                    please contact ROTHCO immediately for compliance information.</p>
                            </div>
                        </div>
                        <div v-show="showCertifyWarning" class="field-state input-folder">
                            <p class="input-support-text invalid-state certify-warning">Please review the above conditions.</p>
                        </div>
                    </div>

                    <div class="input-row">
                        <div class="field-state input-folder">
                            <div class="input-folder--input">
                                <PrimeInputText 
                                    v-model="form.shipping_email"
                                    type="text" 
                                    id="shipping_email" 
                                    :class="{'invalid-state-input': form.errors.shipping_email}"
                                    placeholder=" " 
                                    aria-label="Enter email for tracking (optional)"
                                    name="shipping_email"
                                    autocomplete="false"
                                />
                                <label for="shipping_email">
                                    <p class="paragraph-default">Email Tracking To: (Optional)</p>
                                </label>
                            </div>
                            <div v-if="form.errors.shipping_email" class="field-state input-folder">
                                <p class="input-support-text invalid-state">
                                    {{ form.errors.shipping_email }}
                                </p>
                            </div>
                        </div>

                        <div class="field-state input-folder phone-input">
                            <div class="input-folder--input">
                                <InputMask
                                    id="shipping_phone" 
                                    v-model="form.shipping_phone"
                                    :class="{'invalid-state-input': form.errors.shipping_phone}"
                                    mask="(999) 999-9999"
                                    placeholder=" "
                                    fluid
                                    aria-label="Enter your phone number (optional)"
                                    name="shipping_phone"
                                    autocomplete="false"
                                />
                                <label for="shipping_phone">
                                    <p class="paragraph-default">Phone (Optional)</p>
                                </label>
                            </div>
                            <div v-if="form.errors.shipping_phone" class="field-state input-folder">
                                <p class="input-support-text invalid-state">
                                    {{ form.errors.shipping_phone }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SHIPPING OPTIONS -->
        <div class="b2b-shipping-summary b2b-shipping-options">
            <div class="b2b-card">
                <div class="header">
                    <h2 class="title-l">Shipping Options</h2>
                </div>
                <div class="content">
                    <div class="input-row" v-if="canSetMustShipDate">
                        <date-picker 
                            label="Must Ship On (Optional)" 
                            v-model="form.shipping_must_ship_date" 
                            :class="{'invalid-state-input': form.errors.shipping_must_ship_date}"
                            name="shipping_must_ship_date" 
                            id="shipping_must_ship_date" 
                            :min-date="props.mustShip.can_ship_today ? new Date() :   new Date().setDate(new Date().getDate() + 1)"
                            aria-label="Select must ship date"
                            autocomplete="false"
                            :disabled-week-days="[0, 6]"
                        />
                        <div v-if="form.errors.shipping_must_ship_date" class="field-state input-folder">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.shipping_must_ship_date }}
                            </p>
                        </div>
                    </div>
                    <div class="input-row">
                        <div class="field-state order-comments">
                            <div class="textarea-folder">
                                <PrimeTextArea 
                                    v-model="form.shipping_comments"
                                    id="shipping_comments"
                                    name="shipping_comments"
                                    placeholder=" " class="input-folder--input">
                                </PrimeTextArea>
                                <label class="paragraph-default" for="shipping_comments">Shipping Comments (Optional)</label>
                            </div>
                        </div>
                        <div class="input-details">
                            <p class="paragraph-details">NOTE: writing “must ship” notes in these comments will hold your order for review and may
                                delay processing. Please use the “Must Ship On” date selector above to indicate your desired
                                ship date.</p>
                            <p class="paragraph-details">To remove saved default comments, delete the comment & check “Save Defaults” below.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div> 

        <!-- SHIP BLIND -->
        <div class="notification-bar-wrapper">  
            <div class="drop-ship-banner">
                <span class="drop-ship-leftside">
                    <p class="title-s banner-title">
                        Ship This Order Blind?
                    </p>
                    <span class="cta-wrapper">
                        <button id="ship_blind_no" type="button" :disabled="isDropShip" :class="[getToggleButtonClasses({active: !isShipBlind, disabled: isDropShip})]" 
                            @click="setShipBlind(0)" aria-label="Decline blind shipping">
                            No
                        </button>
                        <button id="ship_blind_yes" type="button" :disabled="isDropShip" :class="[getToggleButtonClasses({active: isShipBlind, disabled: isDropShip})]" 
                            @click="setShipBlind(1)" aria-label="Accept blind shipping">
                            Yes
                        </button>
                    </span>
                    <p>We’ll remove all packing slips, invoices, pricing documents, and branded packing materials from your
                        shipment</p>
                </span>
            </div>
        </div>
        
        <!-- SHIPPING METHODS -->
        <div id="shipping_methods_section" class="b2b-shipping-summary">
            <div class="b2b-card shipping-methods">
                <div class="header">
                    <h2 class="title-l">Shipping Method</h2>
                </div>
                <div class="content">
                    <custom-select label="Shipping Methods" :options="formattedAvailableShippingServiceLevels" v-model="shippingServiceLevel" :error="form.errors.shipping_service_level != null"/>
                    <div class="field-state input-folder"><p class="input-support-text invalid-state">{{ form.errors.shipping_service_level }}</p></div>
                    <Checkbox
                        v-show="showAdvancedOptionsCheckbox"
                        v-model="options.show_advanced_shipping_options"
                        id="show_advanced_shipping_options"
                        class="advanced-shipping" 
                        label="Advanced Shipping Options"
                        @input="clearAdvancedShippingOptions()"
                    />

                    <div class="input-row">
                        <custom-select :options="formattedAvailableShippingMethods" v-model="shippingMethod" label="Shipping Partner"/>
                        <custom-select :options="freightPaymentTerms" v-model="freightPaymentTermComputed" label="Payment Collection" id="freight_payment_term"/>
                    </div>
                    <div v-if="form.freight_payment_term == 'Collect'" class="input-row">
                        <custom-select :options="formattedAvailableCollectAccounts" v-model="collectAccount" label="Collect Number" id="collect_number"/>
                        <div class="field-state input-folder">
                            <div class="input-folder--input" v-if="!form.collect_no_id">
                                <PrimeInputText 
                                    v-model="form.new_collect_no"
                                    maxlength="30"
                                    type="text" 
                                    id="new_collect_no" 
                                    placeholder=" " 
                                    aria-label="Enter new collect number"
                                    name="new_collect_no"
                                    autocomplete="false"
                                    :class="{'invalid-state-input': form.errors.new_collect_no}"
                                />
                                <label for="new_collect_no">
                                    <p class="paragraph-default">New Collect Number</p>
                                </label>
                            </div>
                            <div v-if="form.errors.new_collect_no" class="field-state input-folder">
                                <p class="input-support-text invalid-state">
                                    {{ form.errors.new_collect_no }}
                                </p>
                            </div>
                            <div v-else-if="form.freight_payment_term == 'Collect' && form.collect_no_id" style="height: 100%; display:flex; align-items: center;">
                                <RothcoCta ctaType="link" @click="deleteCollectAccount()">
                                    Delete Collect Number
                                </RothcoCta>
                            </div>
                        </div>
                    </div>
                    <hr>
                    <p class="shipping-method-copy paragraph-details">Please note all Rothco orders are made F.O.B. from our Ronkonkoma, NY warehouse. All shipping
                        charges to final destination are the responsibility of the purchaser.
                    </p>
                </div>
            </div>
        </div>
        <div class="b2b-shipping-summary">
            <div class="b2b-card">
                <div class="header">
                    <h2 class="title-l">Save Shipping Information as Default</h2>
                </div>
                <div class="content">
                    <Checkbox
                        v-model="form.default_shipping"
                        id="default_shipping"
                        label="Save Default Shipping Settings to Enable Express Checkout on Future Orders"
                    />
                </div>
            </div>
        </div>
        <!-- CONTINUE -->
        <RothcoCta @click.prevent="saveShipping()" type="submit" class="primary billing-btn" aria-label="Continue to billing and payment" :disabled="form.processing">
            Continue to billing & payment
        </RothcoCta>
    </form>
</template>

<script setup lang="ts">
    import {
        ref,
        computed,
        watch,
        watchEffect,
        onMounted,
        nextTick,
        inject
    } from "vue";
    import type { PropType, } from "vue";
    import AddressInput from "@/components/AddressInput.vue";
    import Checkbox from "@/components/Checkbox.vue";
    import RothcoCta from "@/components/RothcoCta.vue"
    import DeleteCollectAccountModal from "@/components/DeleteCollectAccountModal.vue";
    import RequestNewShippingAddress from "@/components/RequestNewShippingAddress.vue";
    import { useForm, getFirstInvalidInputElement } from "@/helpers/composables/useForm";
    import { onClickOutside } from '@vueuse/core'
    import { string, number, date, object } from "yup";
    import { useRouter } from "@/helpers/composables/useRouter";
    import { type InertiaForm, router, usePage } from "@inertiajs/vue3";
    import DatePicker from "@/components/DatePicker.vue";
    // import { route } from "ziggy-js";
    import { useDialog } from "primevue/usedialog";
    import moment from "moment";
    import RedQuestion from "@icons/red-question.svg";
    import _ from "lodash";
    import useGoogleManager from "@/helpers/composables/google";

    interface ShippingForm {
        shipping_customer_code: string;
        shipping_save_dropship: number;
        shipping_name: string;
        shipping_in_care_of: string;
        shipping_address: string;
        shipping_address_2: string;
        shipping_city: string;
        shipping_state: string;
        shipping_zip: string;
        shipping_country: string;
        shipping_final_destination_country: string;
        shipping_final_destination_state: string;
        shipping_phone: string;
        shipping_email: string;
        shipping_must_ship_date: string;
        shipping_comments: string;
        drop_ship: number;
        ship_blind: number;
        shipping_service_level: string;
        shipping_method: string|ShippingMethod;
        freight_payment_term: string;
        collect_no_id: string;
        new_collect_no: string;
        default_shipping: number;
    }

    interface ShiptoAddress {
        code: string;
        name: string;
        in_care_of: string;
        street1: string;
        street2: string;
        city: string;
        state: string;
        zip: string;
        country: string;
        final_addresses: FinalAddress[];
        default_shipping_method: string;
    }
    interface FinalAddress {
        country: string;
        country_name: string;
        state?: string;
    }

    interface Region {
        code: string;
        name: string;
    }

    interface Country {
        alpha3code: string;
        alpha2code: string;
        name: string;
    }

    interface CollectAccount {
        collect_no: string;
        shipping_method: string;
        company_id: string | number;
        id: string | number;
    }

    interface ServiceLevel {
        code: string;
        name: string;
        type: string;
        shippingMethods: ShippingMethod[];
        defaultShippingMethod?: ShippingMethod;
    }

    interface ShippingMethod {
        code: string;
        name: string;
    }
    let route = inject("route");
    let { to } = useRouter({
        router,
        route
    });

    const props = defineProps({
        step: {
            type: String
        },
        shipTos: {
            type: Array as PropType<ShiptoAddress[]>,
            default: () => []
        },
        dropShipAddresses: {
            type: Array as PropType<ShiptoAddress[]>,
            default: () => []
        },
        shippingServicesLevels: {
            type: Array as PropType<ServiceLevel[]>,
            default: () => []
        },
        collectAccounts: {
            type: Array as PropType<CollectAccount[]>,
            default: () => []
        },
        regions: {
            type: Array as PropType<Region[]>,
            default: () => []
        },
        countries: {
            type: Array as PropType<Country[]>,
            default: () => []
        },
        mustShip: {
            type: Object,
            default: () => {
                return {};
            }
        },
        sessionShipping: {
            type: Object,
            default: () => {
                return {};
            }
        },
        defaultCheckoutOptions: {
            type: Object,
            default: () => {
                return {};
            }
        },
        errors: {
            type: Object,
            default: () => {
                return {};
            }
        },
    });

    const phoneRegExp = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

    const form = useForm({
        useInertia: true,
        inertiaAlias: "Dealer/Shipping",
        schema: object({
            shipping_customer_code: string()
                .notRequired()
                .default(getDefault("shipping_customer_code")),
            shipping_save_dropship: number().default(0),
            shipping_name: string()
                .required("Shipping Name is required")
                .default(getDefault("shipping_name")),
            shipping_in_care_of: string()
                .notRequired()
                .default(getDefault("shipping_in_care_of")),
            shipping_address: string()
                .required("Street 1 is required")
                .default(getDefault("shipping_address")),
            shipping_address_2: string()
                .notRequired()
                .default(getDefault("shipping_address_2")),
            shipping_city: string()
                .when("shipping_country", {
                    is: (val) => ["US", "USA"].includes(val),
                    then: (schema) =>
                        schema.required("City is required for USA"),
                    otherwise: (schema) => schema.notRequired()
                })
                .default(getDefault("shipping_city")),
            shipping_state: string()
                .when("shipping_country", {
                    is: (val) => ["US", "USA"].includes(val),
                    then: (schema) =>
                        schema.required("State is required for USA"),
                    otherwise: (schema) => schema.notRequired()
                })
                .default(getDefault("shipping_state")),
            shipping_zip: string()
                .when("shipping_country", {
                    is: (val) => ['US','USA'].includes(val),
                    then: (schema) =>
                        schema.required("Zip is required for USA"),
                    otherwise: (schema) => schema.notRequired()
                })
                .default(getDefault("shipping_zip")),
            shipping_country: string()
                .required("Country is required")
                .default(getDefault("shipping_country")),
            shipping_final_destination_country: string()
                .required("Final Destination Country is required")
                .default(getDefault("shipping_final_destination_country")),
            shipping_final_destination_state: string()
                .when("shipping_final_destination_country", {
                    is: (val) => ["US", "USA"].includes(val),
                    then: (schema) =>
                        schema.required(
                            "Final Destination State is required for USA"
                        ),
                    otherwise: (schema) => schema.notRequired()
                })
                .default(getDefault("shipping_final_destination_state")),
            shipping_phone: string().notRequired()
                .matches(phoneRegExp, {
                    message: "Phone number is not valid",
                    excludeEmptyString: true
                })
                .default(getDefault("shipping_phone")),
            shipping_email: string()
                .email("Please enter a valid email")
                .notRequired()
                .default(getDefault("shipping_email", usePage()?.props?.user?.email)),
            shipping_must_ship_date: date()
                .notRequired()
                .test("shipping_must_ship_date", "", (value, ctx) => {
                    if (!value) return true;

                    let isMustShipToday = moment(value)
                        .startOf("day")
                        .isSame(moment(new Date()).startOf("day"));
                    if (isMustShipToday) {
                        if (!props.mustShip.can_ship_today) {
                            return ctx.createError({
                                message:
                                    "Must Ship Date invalid - cannot ship today"
                            });
                        }
                    } else {
                        if (
                            moment(value)
                                .startOf("day")
                                .isBefore(moment(new Date()).startOf("day"))
                        ) {
                            return ctx.createError({
                                message:
                                    "Must Ship Date invalid - cannot be earlier than today"
                            });
                        }else if([0,6].includes(moment(value).day())){
                            return ctx.createError({
                                message:"Must Ship Date invalid - cannot select a weekend date"
                            });
                        }
                    }
                    return true;
                })
                .default(() => {
                    let defaultDate = getDefault("shipping_must_ship_date");
                    return defaultDate ? moment(defaultDate).toDate() : null;
                }),
            shipping_comments: string()
                .notRequired()
                .default(getDefault("shipping_comments")),
            drop_ship: number().default(getDefault("drop_ship", 0)),
            ship_blind: number().default(getDefault("ship_blind", 0)),
            shipping_service_level: string()
                .required("Shipping Service Level is required")
                .default(getDefault("shipping_service_level")),
            shipping_method: string()
                .required("Shipping Method is required")
                .default(getDefault("shipping_method")),
            freight_payment_term: string()
                .required("Shipping Terms is required")
                .default(
                    getDefault("freight_payment_term", 'BillWithShipment')
                ),
            collect_no_id: string()
                .when("freight_payment_term", {
                    is: (val) => (val === "Collect" && collectAccount.value.value != "New Collect Number"),
                    then: (schema) =>
                        schema.required(
                            "Collect Account required for Collect Shipping Terms"
                        ),
                    otherwise: (schema) => schema.notRequired()
                })
                .default(getDefault("collect_no_id")),
            new_collect_no: string()
                .max(30)
                .when(["freight_payment_term", "collect_no_id"], {
                    is: (freight_payment_term, collect_no_id) =>
                        freight_payment_term === "Collect" && !collect_no_id,
                    then: (schema) =>
                        schema.required(
                            "Collect Account required for Collect Shipping Terms"
                        ),
                    otherwise: (schema) => schema.notRequired()
                })
                .default(""),
            default_shipping: number().default(0)
        })
    }) as InertiaForm<ShippingForm>

    let options = ref({
        final_destination_certify: 0,
        show_advanced_shipping_options: 0
    });

    const showCertifyWarning = ref(false);

    const shippingMethodDropdown = ref(null);
    const shippingMethodDropdownToggle = (val)=>{
        return shippingMethodDropdown.value.open = (val === undefined) ? !shippingMethodDropdown.value?.open : val;
    }
    onClickOutside(shippingMethodDropdown, event => shippingMethodDropdownToggle(false))
    let { gtmCustomEvent } = useGoogleManager();
    onMounted(() => {
        // Set default shipto customer
        gtmCustomEvent('b2b_checkout_shipping_screen')

        if (!form.shipping_customer_code && !isDropShip.value) {
            selectShipTo({
                code: shipTosData.value?.[0].code,
                shouldSetDefaults: false
            });
        }

        // Set default service level / method
        if (!form.shipping_method) {
            shippingServiceLevel.value = {
                key: availableServiceLevels.value?.[0]?.code
            }
        }else{
            let isDefaultShippingMethod = availableServiceLevels.value?.some((serviceLevel) => {
                return serviceLevel.defaultShippingMethod?.code === form.shipping_method;
            });
            if(!isDefaultShippingMethod){
                options.value.show_advanced_shipping_options = 1;
            }
        }

       
        if (form.freight_payment_term !== "BillWithShipment" ) {
            options.value.show_advanced_shipping_options = 1;
        }
    });

    const defaultFreightPaymentTerm = computed(()=>{
        const currentShipTo = form.shipping_customer_code ? props.shipTos?.find((shipTo)=>(shipTo.code == form.shipping_customer_code)) : null;
        return currentShipTo?.freight_payment_term ?? null;
    })
    const freightPaymentTerms = computed(()=>{
        let arr = [
            { key: "BillWithShipment", value: "Bill With Shipment" },
            { key: "Collect", value: "Collect" },
        ]

        if(defaultFreightPaymentTerm.value 
            && !arr.some((freightPayTerms)=>(freightPayTerms.key == defaultFreightPaymentTerm.value.code)) && (defaultFreightPaymentTerm.value.code != 'Prepaid')){
            if(defaultFreightPaymentTerm.value.code == 'None'){
                arr.unshift({ key: defaultFreightPaymentTerm.value.code, value: defaultFreightPaymentTerm.value.name });

            }else{
                arr.push({ key: defaultFreightPaymentTerm.value.code, value: defaultFreightPaymentTerm.value.name });
            }
        }
        return arr;
    });
    
    let freightPaymentTermComputed = computed({
        get(){
            return freightPaymentTerms.value.find(term=>term.key == form.freight_payment_term) ?? freightPaymentTerms.value[0];
        },
        set(newVal){
            if(newVal.key === 'Prepaid'){
                newVal.key = 'BillWithShipment';
            }

            form.freight_payment_term = newVal.key;
            if(newVal.key != "Collect"){
                clearCollectAccount();
            }
            
            if(newVal.key !== 'BillWithShipment'){
                options.value.show_advanced_shipping_options = 1;
            }
        }
    });

    function getDefault(
        fieldName: string,
        initialValue: string | number | null = null
    ) { 

        let isShipToField = ['shipping_name', 'shipping_in_care_of', 'shipping_address', 'shipping_address_2', 
            'shipping_city', 'shipping_state', 'shipping_zip', 'shipping_country', 'shipping_phone'].includes(fieldName);
        
        if(isShipToField){
            if(props.sessionShipping['shipping_customer_code']){
                return props.sessionShipping[fieldName];
            }
            else if(props.defaultCheckoutOptions['shipping_customer_code']){
                return props.defaultCheckoutOptions[fieldName];
            }
        }

        // prevent setting collect_no_id when current session/defaults does not have Collect terms
        if(fieldName === 'collect_no_id' && (props.sessionShipping[fieldName] || props.defaultCheckoutOptions[fieldName])){
            if((props.sessionShipping['freight_payment_term'] || props.defaultCheckoutOptions['freight_payment_term'] || "BillWithShipment") != "Collect"){
                return initialValue;
            }
        }

        // prevent setting final destination state if final destination country is not USA
        if(fieldName === 'shipping_final_destination_state' && (props.sessionShipping[fieldName] || props.defaultCheckoutOptions[fieldName])){
            let country = props.sessionShipping['shipping_final_destination_country'] || props.defaultCheckoutOptions['shipping_final_destination_country']
            if(!['US','USA'].includes(country)){
                return initialValue;
            }
        }

        if(!["",null,undefined].includes(props.sessionShipping[fieldName])){
            return props.sessionShipping[fieldName];
        }else if(!["",null,undefined].includes(props.defaultCheckoutOptions[fieldName])){
            return props.defaultCheckoutOptions[fieldName];
        }else{
            return initialValue;
        }
    }

    let shipTosData = computed(() => {
        return isDropShip.value ? props.dropShipAddresses?.map(addr=>({...addr, country: 'USA'})) : props.shipTos;
    });

    let formattedShipTos = computed(()=>{
        return shipTosData.value.map(shipTo=>{
            return {
                id: shipTo.code,
                key: shipTo.code,
                value: shipTo.name
            }
        })
    })

    const dropshipAddressIsValid = ref(false);

    let shipTo = computed({
        get() {
            return formattedShipTos.value.find(
                (shipTo) => shipTo.key == form.shipping_customer_code
            );
        },
        set(newVal) {
            selectShipTo({code: newVal.id})
        }
    })
    let selectShipTo = (params) => {
        let { code, shouldSetDefaults = true } = params;

        form.shipping_customer_code = code;

        let selectedShipTo = shipTosData.value.find(
            (shipTo) => shipTo.code == code
        ) ?? { country: "USA" };
        if (selectedShipTo) {
            setAddress(selectedShipTo);

            // should only set these fields if shipto selected manually, or when shipto is selected on initial visit and fields are not set on session/defaults
            if (shouldSetDefaults || (!props.sessionShipping['freight_payment_term'] && !props.defaultCheckoutOptions['freight_payment_term'])) {
                // default freight payterms
                if(selectedShipTo.freight_payment_term){
                    freightPaymentTermComputed.value = {key: selectedShipTo.freight_payment_term.code, value: selectedShipTo.freight_payment_term.name};
                }
            }
            if (shouldSetDefaults || (!props.sessionShipping['shipping_method'] && !props.defaultCheckoutOptions['shipping_method'])){
                // default shipping methods
                if (selectedShipTo.default_shipping_method) {
                    shippingMethod.value = {key: selectedShipTo.default_shipping_method};
                } else {
                    shippingServiceLevel.value = {
                        key: availableServiceLevels.value?.[0]?.code
                    }
                }
            }
            if (shouldSetDefaults || (!props.sessionShipping['shipping_final_destination_country'] && !props.defaultCheckoutOptions['shipping_final_destination_country'])){
                // default final address from shipto
                if (isDropShip.value) {
                    finalDestinationAddress.value = formattedCountries.value.find(cntry=>cntry.key == form.shipping_country) ?? {key: "", value: ""};
                    finalDestinationState.value = form.shipping_state && form.shipping_country === 'USA' ? {key: form.shipping_state} : null;
                } else {
                    if (finalAddresses.value.length > 1 || finalAddresses.value?.some((finalAddr) => ["RSUS", "RSTR"].includes(finalAddr.country))) {
                        finalDestinationAddress.value = null;
                    } else {
                        finalDestinationAddress.value = {
                            key:  finalAddresses.value[0]?.mappedValue ?? null
                        }
                           
                    }
                }
            }
        } else {
            setAddress(null);
        }
    };
    let setAddress = (newVal) => {
        form.shipping_customer_code = newVal?.code ?? "";
        form.shipping_name = newVal?.name ?? "";
        form.shipping_in_care_of = newVal?.in_care_of ?? "";
        form.shipping_address = newVal?.street1 ?? "";
        form.shipping_address_2 = newVal?.street2 ?? "";
        form.shipping_city = newVal?.city ?? "";
        form.shipping_state = newVal?.state ?? "";
        form.shipping_zip = newVal?.zip ?? "";
        form.shipping_country = newVal?.country ?? "";
        form.shipping_phone = newVal?.phone1 ?? "";
    };
    let addressErrors = computed(() => {
        let keys = [
            "name",
            "in_care_of",
            "address",
            "address_2",
            "city",
            "state",
            "zip",
            "country"
        ];
        return keys.reduce((acc, key) => {
            acc[key] = form.errors?.[`shipping_${key}`];
            return acc;
        }, {});
    });

    const getToggleButtonClasses = ({active, disabled})=>{
        if(disabled){
            return active ? ['disabled small red-btn'] : ['disabled small'];
        }else{
            return active ? ['primary small red-btn'] : ['secondary small notification-btn'];
        }
    }
    let isDropShip = computed(() => {
        return form.drop_ship == 1;
    });
    const setDropShip = (val)=>{
        form.drop_ship = val;
    }
    watch(isDropShip, (newVal) => {
        if (newVal) {
            setAddress(null);
            form.shipping_customer_code = "";
            form.shipping_country = "USA";
            form.ship_blind = 1;
            form.default_shipping = 0;
        } else {
            selectShipTo({ code: props.shipTos[0]?.code });
            form.shipping_save_dropship = 0;
        }
    });
    watchEffect(()=>{
        if(isDropShip.value && (form.shipping_country || form.shipping_state)){
            form.shipping_final_destination_country = form.shipping_country;
            form.shipping_final_destination_state = form.shipping_state;
        }
    })

    const setShipBlind = (val)=>{
        form.ship_blind = val;
    }
    const isShipBlind = computed(()=>{
        return form.ship_blind == 1;
    })

    let finalAddresses = computed(() => {
        let shipTo = shipTosData.value.find(
            (shipTo) => form.shipping_customer_code == shipTo.code
        );
        return (
            shipTo?.final_addresses?.map((finalAddr) => {
                return {
                    ...finalAddr,
                    mappedValue:
                        finalAddr.country == "USA" && finalAddr.state
                            ? `USA:${finalAddr.state ?? ""}`
                            : finalAddr.country
                };
            }) ?? []
        );
    });

    let availableFinalAddresses = computed(() => {
        return finalAddresses.value.filter(
            (finalAddr) => !["RSUS", "RSTR"].includes(finalAddr.country)
        );
    });

    let formattedAvailableFinalAddresses = computed(()=>{
        return availableFinalAddresses.value.map((finalAddress)=>{
            return {
                value: finalAddress.country == "USA" && finalAddress.state ? `${finalAddress.state}, United States` : finalAddress.country_name,
                key: finalAddress.mappedValue
            }
        })
    })

    const exporterFinalDestination = computed(()=>{
        return {
            value: 'Multiple Countries Outside USA',
            key: ['US','USA'].includes(form.shipping_country) ? 'RSUS' : 'RSTR',
        }
    })
    const isExporter = computed(()=>{
        return ['RSUS','RSTR'].includes(form.shipping_final_destination_country);
    })

    let uniqueFinalDestinationOptions = computed(()=>{
        const arr = [
            exporterFinalDestination.value,
            ...formattedAvailableFinalAddresses.value, 
            ...formattedCountries.value
        ];
        return _.uniqBy(arr, 'key');
    })

    let formattedCountries = computed(()=>{
        return props?.countries?.map((country)=>{
            return {
                value: country.name,
                key: country.alpha3code
            }
        }) ?? [];
    })

    let finalDestinationAddress = computed({
        get() {
            let addr = {
                value: "",
                key: ""
            };
            if (form.shipping_final_destination_country) {
                if (form.shipping_final_destination_country == "USA") {
                    let val = `USA${form.shipping_final_destination_state ? `:${form.shipping_final_destination_state}` : ""}`;
                    let finalAddressWithState = formattedAvailableFinalAddresses.value.find((finalAddress) => finalAddress.key == val);
                    addr = finalAddressWithState ?? {
                        value: "United States",
                        key: "USA"
                    } ;
                } else {
                    addr = uniqueFinalDestinationOptions.value.find((finalAddress) => finalAddress.key == form.shipping_final_destination_country) ??
                        {
                            value: "",
                            key: ""
                        };
                }
            }
            return addr;
        },
        set(newVal) {
            let finalAddress = finalAddresses.value.find((finalAddr) => finalAddr.mappedValue == newVal?.key);
            if(finalAddress){
                form.shipping_final_destination_country = finalAddress?.country;
                form.shipping_final_destination_state = finalAddress?.state ?? null;
            }else{
                let finalDest = uniqueFinalDestinationOptions.value.find((finalDest)=> finalDest.key == newVal?.key);
                form.shipping_final_destination_country = finalDest?.key ?? "";
            }
            if(!['US','USA'].includes(form.shipping_final_destination_country)){
                form.shipping_final_destination_state = "";
            }
        }
    });

    let formattedFinalDestinationStates = computed(()=>{
        return [
            {
                value: 'Multiple States/Regions',
                key: 'None'
            },
            ...props.regions.filter((region)=>(region.country === "USA")).map((region)=>{
                return {
                    value: region.name,
                    key: region.code
                }
            })
        ]
    })

    let finalDestinationState = computed({
        get(){
            return formattedFinalDestinationStates.value.find(region=>region.key == form.shipping_final_destination_state) ?? {key: "", value: ""};
        },
        set(newVal){
            form.shipping_final_destination_state = newVal.key;
        }
    })
    watch(
        [
            () => form.shipping_final_destination_country,
            () => form.shipping_final_destination_state
        ],
        () => {
            options.value.final_destination_certify = 0;
        }
    );

    watch(()=>options.value.final_destination_certify, (newVal,oldVal)=>{
        if(newVal != oldVal) showCertifyWarning.value = false;
    })

    let canSetMustShipDate = computed(() => {
        return props.mustShip.isShowMustShipDate;
    });

    let disabledDates = computed(() => {
        if(!props.mustShip.can_ship_today){
            return [new Date()];
        }
        return [];
    });

    // SHIPPING METHODS
    let serviceLevelsType = computed(() => {
        switch (form.shipping_country) {
            case "USA":
                return "Domestic";
            case "CAN":
                return "Canada";
            default:
                return "Export";
        }
    });
    let availableServiceLevels = computed(() => {
        let arr = props.shippingServicesLevels.filter((serviceLevel) => {
            return serviceLevel.type == serviceLevelsType.value;
        });
        if (form.shipping_country == "USA") {
            arr.push({
                code: "PICKUP",
                name: "Pick up at Rothco",
                type: "Domestic",
                shippingMethods: [{ code: "PICKUP", name: "PICKUP" }]
            });
        }
        return arr;
    });
    let formattedAvailableShippingServiceLevels = computed(()=>{
        return availableServiceLevels.value.map((level)=>{
            return {
                value: level.name,
                key: level.code
            }
        });
    })
    let shippingServiceLevel = computed({
        get(){
            return {
                value: availableServiceLevels.value.find(level=>level.code == form.shipping_service_level)?.name ?? "",
                key: form.shipping_service_level
            }
        },
        set(newVal){
            form.shipping_service_level = newVal.key;
            setDefaultShippingMethodFromServiceLevel(form.shipping_service_level);
        }
    })
    const setDefaultShippingMethodFromServiceLevel = (serviceLevelCode)=>{
        let serviceLevel = availableServiceLevels.value.find(level=>level.code == serviceLevelCode);
        if(serviceLevel){
            shippingMethod.value = serviceLevel?.defaultShippingMethod?.code ? {
                key: serviceLevel.defaultShippingMethod.code
            } : {
                key: availableShippingMethods.value[0]?.code
            }
        }
    }

    let availableShippingMethods = computed(() => {
        let currentLevel = availableServiceLevels.value.find(
            (serviceLevel) => serviceLevel.code == shippingServiceLevel.value?.key
        ) ?? { shippingMethods: [] };
        return currentLevel.shippingMethods;
    });
    let formattedAvailableShippingMethods = computed(()=>{
        return availableShippingMethods.value.map((method)=>{
            return {
                value: method.name,
                key: method.code
            }
        });
    });
    let shippingMethod = computed({
        get(){
            return {
                value: availableShippingMethods.value.find(method=>method.code == form.shipping_method)?.name ?? "",
                key: form.shipping_method
            }
        },
        set(newVal){
            form.shipping_method = newVal.key;
        }
    })

    watch(
        [() => form.shipping_method, serviceLevelsType],
        (
            [newShippingMethod, newServiceLevelType],
            [oldShippingMethod, oldServiceLevelType]
        ) => {
            if (
                newShippingMethod != oldShippingMethod ||
                newServiceLevelType != oldServiceLevelType
            ) {
                let serviceLevel = availableServiceLevels.value.find(
                    (level) => {
                        return level.shippingMethods.some(
                            (shippingMethod) =>
                                shippingMethod.code == form.shipping_method
                        );
                    }
                ) ?? { code: "" };
                form.shipping_service_level = serviceLevel.code;
            }
        },
        { immediate: true }
    );

    // COLLECT ACCOUNTS

    let dialog = useDialog();
    function deleteCollectAccount() {
        dialog.open(DeleteCollectAccountModal, {
            props: {
                modal: true,
                header: "Delete List Collect Account",
                showHeader: false
            },
            data: {
                collect_account_id: form.collect_no_id,
                afterSubmit: ()=>{
                    to({
                        newParams: {},
                        options: { preserveScroll: true, preserveState: true, only: ['collectAccounts'] }
                    })
                }
            }
        });
    }
    function requestNewShippingAddress() {
        dialog.open(RequestNewShippingAddress, {
            data: {
                countries: props.countries,
                regions: props.regions
            },
            props: {
                showHeader: false,
                modal: true
            },
        });
    };

    let availableCollectAccounts = computed(() => {
        return [
            ...props.collectAccounts.filter((collectAccount) => {
                return collectAccount.shipping_method == form.shipping_method;
            })
        ];
    });

    let formattedAvailableCollectAccounts = computed(()=>{
        return [
            {
                value: 'New Collect Number',
                key: null
            },
            ...availableCollectAccounts.value.map((account)=>{
                return {
                    value: account.collect_no,
                    key: account.id
                }
            })
        ];
    });

    let collectAccount = computed({
        get(){
            return {
                value: availableCollectAccounts.value.find(account=>account.id == form.collect_no_id)?.collect_no ?? "New Collect Number",
                key: form?.collect_no_id ?? null
            }
        },
        set(newVal){
            form.collect_no_id = newVal.key;
            if(newVal?.key){
                clearCollectAccountNumber();
            }
        }
    });
    let showAdvancedOptionsCheckbox = computed(() => {
        return shippingServiceLevel.value?.key != "PICKUP";
    });
    watch(showAdvancedOptionsCheckbox, (newVal) => {
        if (newVal === false) {
            options.value.show_advanced_shipping_options = 0;
            clearAdvancedShippingOptions();
        }
    });
    let showAdvancedOptions = computed(() => {
        return options.value.show_advanced_shipping_options;
    });
    watchEffect(() => {
        if (showAdvancedOptions.value) {
            let collectNumberIsAvailable = availableCollectAccounts.value.some(
                (collectAccount) => collectAccount.id == form.collect_no_id
            );
            let isUsingCollectAccount = form.freight_payment_term == "Collect";
            if (!collectNumberIsAvailable || !isUsingCollectAccount) {
                form.collect_no_id = "";
            } else if (!form.collect_no_id) {
                form.new_collect_no = "";
            }
        }
    });
    let clearAdvancedShippingOptions = () => {
        form.freight_payment_term = "BillWithShipment";
        clearCollectAccount();
    };

    let clearCollectAccount = () => {
        form.collect_no_id = "";
        clearCollectAccountNumber();
    };

    let clearCollectAccountNumber = () => {
        form.new_collect_no = "";
    };
    
    let canSubmitForm = computed(() => {
        return (options.value.final_destination_certify == 1 || isExporter.value)
            && Object.values(form.errors).length == 0
    });

    let saveShipping = () => {
        if(form.processing) return;
        
        if (canSubmitForm.value) {
            gtmCustomEvent('add_shipping_info', {
                currency: 'USD'
            })
            form.transform((data) => {
                if(data.freight_payment_term != "Collect"){
                    data.collect_no_id = "";
                }
                if (data.freight_payment_term != "Collect" || !!data.collect_no_id) {
                    data.new_collect_no = "";
                }
                if(!['US','USA'].includes(data.shipping_final_destination_country) || data.shipping_final_destination_state == 'None'){
                    data.shipping_final_destination_state = "";
                }
                if(['RSUS','RSTR'].includes(data.shipping_final_destination_country)){
                    data.shipping_final_destination_country = ['US','USA'].includes(data.shipping_country) ? 'RSUS' : 'RSTR';
                }

                if(data.shipping_must_ship_date){
                    data.shipping_must_ship_date = moment(data.shipping_must_ship_date).format("YYYY-MM-DD");
                }
                return data;
            }).post(route("checkout.save-shipping"));
        }else{
            // find the first element with an error and scroll to it
            let invalidInputElement = getFirstInvalidInputElement();
            if(invalidInputElement){
                invalidInputElement.scrollToAndFocus();
            }else if(options.value.final_destination_certify == 0){
                let certifyElement = document.querySelector('.certify-checkbox');
                certifyElement?.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
                showCertifyWarning.value = true;
                try{
                    certifyElement?.focus?.();
                }catch(e){
                    console.error(e);
                }
            }
        }
    };
</script>
